import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import SEOValidator from '@/components/SEOValidator';
import CoreWebVitalsMonitor from '@/components/CoreWebVitalsMonitor';
import { SEOHead } from '@/components/SEOHead';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Activity, 
  BarChart3, 
  CheckCircle, 
  Globe, 
  Zap,
  FileText,
  Settings
} from 'lucide-react';

const SEOTestPage: React.FC = () => {
  return (
    <>
      <SEOHead 
        title="SEO技术测试和验证"
        description="全面的SEO技术测试工具，包括meta标签验证、Core Web Vitals监控、结构化数据检查等，帮助优化网站搜索引擎表现。"
        keywords={['SEO测试', 'Core Web Vitals', '性能监控', '技术SEO', '搜索引擎优化', '网站优化']}
        type="article"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* 页面标题 */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
              <Search className="w-10 h-10 text-blue-600" />
              SEO技术测试中心
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              全面检测和优化网站的SEO技术配置，提升搜索引擎排名和用户体验
            </p>
            <div className="flex justify-center gap-2 mt-4">
              <Badge variant="secondary" className="text-sm">
                <Globe className="w-3 h-3 mr-1" />
                技术SEO
              </Badge>
              <Badge variant="secondary" className="text-sm">
                <Activity className="w-3 h-3 mr-1" />
                性能监控
              </Badge>
              <Badge variant="secondary" className="text-sm">
                <BarChart3 className="w-3 h-3 mr-1" />
                实时分析
              </Badge>
            </div>
          </div>

          {/* 主要测试工具 */}
          <Tabs defaultValue="seo-validator" className="space-y-6">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 h-12">
              <TabsTrigger value="seo-validator" className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                SEO验证
              </TabsTrigger>
              <TabsTrigger value="core-vitals" className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                性能监控
              </TabsTrigger>
              <TabsTrigger value="optimization" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                优化指南
              </TabsTrigger>
            </TabsList>

            <TabsContent value="seo-validator" className="space-y-6">
              <SEOValidator />
            </TabsContent>

            <TabsContent value="core-vitals" className="space-y-6">
              <CoreWebVitalsMonitor />
            </TabsContent>

            <TabsContent value="optimization" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* SEO技术优化指南 */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      SEO技术优化
                    </CardTitle>
                    <CardDescription>
                      提升搜索引擎可见性的关键技术要素
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">Meta标签优化</h4>
                          <p className="text-sm text-gray-600">
                            确保每个页面都有独特的标题和描述，长度符合搜索引擎要求
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">结构化数据</h4>
                          <p className="text-sm text-gray-600">
                            使用Schema.org标记帮助搜索引擎更好地理解内容
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">URL结构优化</h4>
                          <p className="text-sm text-gray-600">
                            使用清晰、描述性的URL，设置规范化链接
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">XML网站地图</h4>
                          <p className="text-sm text-gray-600">
                            创建和维护完整的XML sitemap，帮助搜索引擎发现内容
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">Robots.txt配置</h4>
                          <p className="text-sm text-gray-600">
                            正确配置robots.txt，指导搜索引擎爬取策略
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* 性能优化指南 */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-600" />
                      性能优化策略
                    </CardTitle>
                    <CardDescription>
                      提升Core Web Vitals和用户体验
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">图片优化</h4>
                          <p className="text-sm text-gray-600">
                            使用现代图片格式，设置适当尺寸，启用懒加载
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">代码分割</h4>
                          <p className="text-sm text-gray-600">
                            实现JavaScript代码分割和按需加载
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">缓存策略</h4>
                          <p className="text-sm text-gray-600">
                            配置合适的缓存头，使用Service Worker
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">CDN加速</h4>
                          <p className="text-sm text-gray-600">
                            使用内容分发网络加快全球访问速度
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium">资源预加载</h4>
                          <p className="text-sm text-gray-600">
                            预加载关键资源，DNS预解析，资源提示
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* SEO最佳实践 */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    SEO最佳实践检查清单
                  </CardTitle>
                  <CardDescription>
                    确保网站符合现代SEO标准的完整检查清单
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3 text-blue-600">技术SEO</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>HTTPS安全协议</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>移动端友好性</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>页面加载速度优化</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>URL结构规范化</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>404错误页面处理</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-3 text-purple-600">内容优化</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>标题层级结构清晰</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>图片Alt属性完整</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>内链建设合理</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>内容原创性高</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>关键词布局自然</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default SEOTestPage;